'use strict';

import moment from 'moment';
import { getMealTypesForDate } from './Meals';

/**
 * Builds available leftover slots for consumer (date-based) modals
 * Starts from next meal slot after main meal, includes 4 days ahead
 *
 * @param {Object} meal - Main meal object with date and meal properties
 * @param {Array} plans - User's eating patterns/plans
 * @param {Object} user - User object with preferences
 * @returns {Array} Array of slot objects with date and mealTypes properties
 *
 * @example
 * const slots = buildConsumerLeftoverSlots(meal, plans, user);
 * // [{ date: '2025-12-09', mealTypes: [{name: 'Dinner'}] }, ...]
 */
export function buildConsumerLeftoverSlots(meal, plans, user, rescheduleMealParams) {
    const mainMealDate = rescheduleMealParams ? rescheduleMealParams.date : moment(meal.date);
    const mainMealType = rescheduleMealParams ? rescheduleMealParams.mealType : meal.meal;

    // Get meal types for the main meal's date (respects eating patterns)
    const mealTypesForMainDate = getMealTypesForDate(mainMealDate, plans, user.preferences.meal_types);

    // Find index of main meal type
    const mainMealIndex = mealTypesForMainDate.findIndex(mt => mt.name === mainMealType);

    // Get remaining meal types after the main meal on same day
    const remainingMealTypesForSameDay = mainMealIndex >= 0
        ? mealTypesForMainDate.slice(mainMealIndex + 1)
        : [];

    // Build available slots
    const availableSlots = [];

    // Add same-day slots if there are remaining meal types after main meal
    if (remainingMealTypesForSameDay.length > 0) {
        availableSlots.push({
            date: mainMealDate.format('YYYY-MM-DD'),
            mealTypes: remainingMealTypesForSameDay
        });
    }

    // Add next 4 days with all meal types
    for (let i = 1; i <= 4; i++) {
        const nextDate = moment(mainMealDate).add(i, 'day');
        const mealTypesForDate = getMealTypesForDate(nextDate, plans, user.preferences.meal_types);

        availableSlots.push({
            date: nextDate.format('YYYY-MM-DD'),
            mealTypes: mealTypesForDate
        });
    }

    return availableSlots;
}

/**
 * Builds available leftover slots for PRO (offset-based) modals
 * Starts from next meal slot after main meal, includes 4 days ahead
 *
 * @param {Object} meal - Main meal object with offset and meal properties
 * @param {Object} plan - Plan object with meal_types array
 * @returns {Array} Array of slot objects with offset and mealTypes properties
 *
 * @example
 * const slots = buildProLeftoverSlots(meal, plan);
 * // [{ offset: 0, mealTypes: [{name: 'Dinner'}] }, ...]
 */
export function buildProLeftoverSlots(meal, plan, rescheduleMealParams) {
    const mainMealOffset = rescheduleMealParams ? rescheduleMealParams.offset : meal.offset;
    const mainMealType = rescheduleMealParams ? rescheduleMealParams.mealType : meal.meal;

    // Get meal types from plan (same for all offsets)
    const allMealTypes = plan.meal_types || [];

    // Find index of main meal type
    const mainMealIndex = allMealTypes.findIndex(mt => mt.name === mainMealType);

    // Get remaining meal types after the main meal on same day (same offset)
    const remainingMealTypesForSameDay = mainMealIndex >= 0
        ? allMealTypes.slice(mainMealIndex + 1)
        : [];

    // Build available slots
    const availableSlots = [];

    // Add same-day slots if there are remaining meal types after main meal
    if (remainingMealTypesForSameDay.length > 0) {
        availableSlots.push({
            offset: mainMealOffset,
            mealTypes: remainingMealTypesForSameDay
        });
    }

    // Add next 4 days with all meal types
    for (let i = 1; i <= 4; i++) {
        const nextOffset = mainMealOffset + i;

        availableSlots.push({
            offset: nextOffset,
            mealTypes: allMealTypes
        });
    }

    return availableSlots;
}

/**
 * Checks if a participant is selected for a specific time slot and meal type
 * Works with both date-based (consumer) and offset-based (PRO) selections
 *
 * @param {Array} selected - Array of selection objects
 * @param {string} timeKeyName - Property name to match ('date' or 'offset')
 * @param {string|number} timeKeyValue - Value to match (date string or offset number)
 * @param {string} mealType - Meal type name
 * @param {string} participantUuid - Participant UUID to check
 * @returns {boolean} True if participant is checked
 *
 * @example
 * isParticipantChecked(selected, 'date', '2025-12-09', 'Dinner', 'USER') // true/false
 * isParticipantChecked(selected, 'offset', 0, 'Dinner', 'USER') // true/false
 */
export function isParticipantChecked(selected, timeKeyName, timeKeyValue, mealType, participantUuid) {
    const match = selected.find(item => item[timeKeyName] === timeKeyValue && item.mealType === mealType);
    return match ? match.participants.some((p) => p.uuid === participantUuid) : false;
}

/**
 * Determines selection state for all participants in a time slot
 * Returns true (all selected), 'partial' (some selected), or false (none selected)
 *
 * @param {Object|null} match - Matched selection object or null
 * @param {Array} participantDetails - Array of all available participant objects
 * @returns {boolean|string} true, 'partial', or false
 */
export function areAllParticipantsSelected(match, participantDetails) {
    if (match) {
        // Count guests (participants with empty uuid)
        const guestCount = match.participants.filter(p => !p.uuid).length;
        // Total available participants = family members + guests
        const totalAvailableParticipants = participantDetails.length + guestCount;

        if (match.participants.length === totalAvailableParticipants) {
            return true;  // All selected (green checkbox)
        } else if (match.participants.length > 0 ) {
            return 'partial';  // Some selected (grey checkbox)
        }
    }

    return false;  // None selected
}

/**
 * Toggles a participant's selection for a specific time slot and meal type
 * Handles adding/removing participants and cleaning up empty selections
 *
 * @param {Array} selected - Current selections array
 * @param {string} timeKeyName - Property name ('date' or 'offset')
 * @param {string|number} timeKeyValue - Time key value
 * @param {string} mealType - Meal type name
 * @param {string} participantUuid - Participant UUID to toggle
 * @param {Array} participantDetails - All available participants
 * @returns {Array} Updated selections array
 */
export function toggleParticipantSelection(selected, timeKeyName, timeKeyValue, mealType, participantUuid, participantDetails) {
    const match = selected.find(item => item[timeKeyName] === timeKeyValue && item.mealType === mealType);
    const selectedWithoutMatch = selected.filter(item => !(item[timeKeyName] === timeKeyValue && item.mealType === mealType));
    const participantExists = match ? match.participants.some((p) => p.uuid === participantUuid) : false;

    if (!match) {
        // Creating new selection - add participant
        const addParticipant = participantDetails.find((p) => p.uuid === participantUuid);
        const newSelection = {
            [timeKeyName]: timeKeyValue,
            mealType: mealType,
            participants: [addParticipant]
        };
        return [...selected, newSelection];
    } else if (participantExists) {
        // Removing participant from existing selection
        const updatedParticipants = match.participants.filter((p) => p.uuid !== participantUuid);

        // If removing last participant, remove selection
        if (updatedParticipants.length === 0) {
            return selectedWithoutMatch;
        }

        const updatedSelection = {
            ...match,
            participants: updatedParticipants
        };
        return [...selectedWithoutMatch, updatedSelection];
    } else {
        // Adding participant to existing selection
        const addParticipant = participantDetails.find((p) => p.uuid === participantUuid);
        const updatedSelection = {
            ...match,
            participants: [...match.participants, addParticipant]
        };
        return [...selectedWithoutMatch, updatedSelection];
    }
}

/**
 * Toggles all family member participants for a time slot and meal type
 * Does not affect guest count - only toggles family members
 *
 * @param {Array} selected - Current selections array
 * @param {string} timeKeyName - Property name ('date' or 'offset')
 * @param {string|number} timeKeyValue - Time key value
 * @param {string} mealType - Meal type name
 * @param {Array} participantDetails - All available family member participants
 * @returns {Array} Updated selections array
 */
export function toggleAllParticipants(selected, timeKeyName, timeKeyValue, mealType, participantDetails) {
    const match = selected.find(item => item[timeKeyName] === timeKeyValue && item.mealType === mealType);
    const allParticipantsSelected = match ? match.participants.length === participantDetails.length : false;
    const filterSelected = selected.filter(item => !(item[timeKeyName] === timeKeyValue && item.mealType === mealType));

    if (allParticipantsSelected) {
        // Deselect all
        return filterSelected;
    } else {
        // Select all participants
        const newSelection = {
            [timeKeyName]: timeKeyValue,
            mealType: mealType,
            participants: participantDetails
        };
        return [...filterSelected, newSelection];
    }
}

/**
 * Updates guest count for a time slot and meal type
 * Preserves non-guest participants, updates guest count
 *
 * @param {Array} selected - Current selections array
 * @param {string} timeKeyName - Property name ('date' or 'offset')
 * @param {string|number} timeKeyValue - Time key value
 * @param {string} mealType - Meal type name
 * @param {number} newGuestCount - New number of guests (>= 0)
 * @param {string} guestNamePrefix - Prefix for guest names (e.g., 'Guest ' or 'Guest #')
 * @returns {Array} Updated selections array
 */
export function updateGuestCount(selected, timeKeyName, timeKeyValue, mealType, newGuestCount, guestNamePrefix = 'Guest ') {
    const match = selected.find(item => item[timeKeyName] === timeKeyValue && item.mealType === mealType);
    const selectedWithoutMatch = selected.filter(item => !(item[timeKeyName] === timeKeyValue && item.mealType === mealType));

    // Get current non-guest participants (guests have uuid === "")
    const nonGuestParticipants = match ? match.participants.filter(p => p.uuid !== "") : [];

    // Create guest objects
    const guests = [...Array(Math.max(0, newGuestCount)).keys()].map(number => ({
        uuid: "",
        name: `${guestNamePrefix}${number + 1}`,
        portion: 1
    }));

    // Combine non-guest and guest participants
    const allParticipants = [...nonGuestParticipants, ...guests];

    if (allParticipants.length === 0) {
        return selectedWithoutMatch;
    } else {
        const updatedSelection = {
            [timeKeyName]: timeKeyValue,
            mealType: mealType,
            participants: allParticipants
        };
        return [...selectedWithoutMatch, updatedSelection];
    }
}

/**
 * Gets current guest count for a time slot and meal type
 *
 * @param {Array} selected - Current selections array
 * @param {string} timeKeyName - Property name ('date' or 'offset')
 * @param {string|number} timeKeyValue - Time key value
 * @param {string} mealType - Meal type name
 * @returns {number} Number of guests (participants with empty uuid)
 */
export function getGuestCount(selected, timeKeyName, timeKeyValue, mealType) {
    const match = selected.find(item => item[timeKeyName] === timeKeyValue && item.mealType === mealType);
    if (!match) return 0;
    return match.participants.filter(p => !p.uuid).length;
}

/**
 * Finds conflicting meals for leftover allocations
 * Checks if leftover is new or existing, only returns conflicts for new leftovers
 *
 * @param {string|number} timeKeyValue - Time key value (date or offset)
 * @param {string} mealType - Meal type name
 * @param {Array} oldLeftovers - Existing leftover objects
 * @param {Array} meals - All meals to check for conflicts
 * @param {string} timeKeyName - Property name ('date'/'leftoverDate' or 'offset'/'leftoverOffset')
 * @param {string} mealTypeKeyName - Property name for meal type ('meal' or 'mealType')
 * @returns {Array} Array of conflicting meal objects (empty if existing leftover)
 */
export function findConflictingMeals(timeKeyValue, mealType, oldLeftovers, meals, timeKeyName, mealTypeKeyName) {
    // Check if this leftover already exists with same time and mealType
    // If only participants changed, it's not a conflict
    const isExistingLeftover = oldLeftovers.some(oldLeftover => {
        // Handle different property name formats
        const oldTime = oldLeftover[timeKeyName] || oldLeftover[`leftover${timeKeyName.charAt(0).toUpperCase()}${timeKeyName.slice(1)}`];
        const oldMealType = oldLeftover[mealTypeKeyName];

        // Normalize dates if needed
        if (timeKeyName === 'date' && typeof oldTime === 'object' && oldTime.format) {
            return oldTime.format('YYYY-MM-DD') === timeKeyValue && oldMealType === mealType;
        }

        return oldTime === timeKeyValue && oldMealType === mealType;
    });

    // Find conflicting meals only if this is a NEW leftover (time/mealType changed)
    // If it's an existing leftover (only participants changed), no conflict
    if (isExistingLeftover) {
        return [];
    }

    return meals.filter(m =>
        m[timeKeyName] === timeKeyValue &&
        m[mealTypeKeyName] === mealType &&
        !m.deleted
    );
}
